import { omit } from 'lodash';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';

import {
  getCarbonFootprint,
  getEquivalents,
  getHotelCarbonFootprint,
  getTransportCarbonFootprint,
} from '../../../api/calculator/calculator';
import {
  GetHotelCarbonFootprintCalculationDTO,
  SegmentT,
} from '../../../api/calculator/calculator.d';
import { getCarbonOffsetProjectList } from '../../../api/carbonOffset/carbonOffset';
import { useCommon } from '../../../context/CommonContext';
import { CalculatorTypeE, TransportTypesE } from '../../../declarations/enums/common';
import { RoutesPathE } from '../../../declarations/enums/routes';
import { TransportI } from '../../../declarations/interfaces/calculator';
import { ContentI } from '../../../declarations/interfaces/carbonOffset';
import { toFixedWithoutZeros } from '../../../helpers/numbers';
import useReactRouter from '../../../hooks/useReactRouter';

export type CarbonOffsetQueryT = {
  id: number;
  qty: number;
};

export type OffsetsT = typeof DEFAULT_OFFSETS;
type ParsedRootCarbonOffsetsT = { selectedQty: number; totalCharge: string } & ContentI;

export type SelectedCarbonOffsetProjectT = Pick<
  ParsedRootCarbonOffsetsT,
  'id' | 'basePrice' | 'selectedQty' | 'name' | 'totalCharge'
>;

type QueryT = {
  hotel: Omit<GetHotelCarbonFootprintCalculationDTO, 'customerId'>;
  carbonOffsets?: CarbonOffsetQueryT[];
  segments: SegmentT[];
  calculate: CalculatorTypeE;
};

const DEFAULT_OFFSETS = {
  stayOffset: 0,
  landOffset: 0,
  planeOffset: 0,
  boatOffset: 0,
  total: 0,
  totalOffsetInTonnes: 0,
};

const useCarbonOffsets = () => {
  const { getUrlQueryParams, navigateTo } = useReactRouter();
  const { hotel, carbonOffsets = [], segments, calculate } = getUrlQueryParams<QueryT>();
  const { customerId } = useCommon();
  const [rootCarbonOffsetProjects, setRootCarbonOffsetProjects] = useState<
    ParsedRootCarbonOffsetsT[]
  >([]);
  const [preparedCarbonOffsetQueryData, setPreparedCarbonOffsetQueryData] =
    useState<CarbonOffsetQueryT[]>(carbonOffsets);
  const [selectedCarbonOffsetProjects, setSelectedCarbonOffsetProjects] =
    useState<SelectedCarbonOffsetProjectT[]>();
  const [totalOffsetInTonnes, setTotalOffsetInTonnes] = useState(0);
  const [offsets, setOffsets] = useState<OffsetsT>(DEFAULT_OFFSETS);

  const getTransportOffsets = (data: TransportI) => {
    return {
      landOffset: data.totalLandTravelValue,
      planeOffset:
        data.transportTypeCFs.find(transport => transport.transportType === TransportTypesE.PLANE)
          ?.value || 0,
      boatOffset:
        data.transportTypeCFs.find(transport => transport.transportType === TransportTypesE.BOAT)
          ?.value || 0,
    };
  };

  const { mutate: getHotelFootprint } = useMutation(
    () => getHotelCarbonFootprint({ customerId, ...hotel }),
    {
      onSuccess({ data }) {
        setOffsets({
          ...offsets,
          stayOffset: data.hotel.value,
          total: data.total,
          totalOffsetInTonnes: data.totalToOffsetInTonnes,
        });
        setTotalOffsetInTonnes(data.totalToOffsetInTonnes);
      },
    }
  );

  const { mutate: getTransportFootprint } = useMutation(
    (segmentList: SegmentT[]) =>
      getTransportCarbonFootprint({ customerId, distanceUnit: 'KM', segments: segmentList }),
    {
      onSuccess({ data }) {
        setOffsets({
          ...offsets,
          total: data.total,
          totalOffsetInTonnes: data.totalToOffsetInTonnes,
          ...getTransportOffsets(data.transport),
        });
        setTotalOffsetInTonnes(data.totalToOffsetInTonnes);
      },
    }
  );

  const { mutate: getCommonCarbonFootprint } = useMutation(
    (segmentList: SegmentT[]) =>
      getCarbonFootprint({
        customerId,
        hotel: { ...hotel, customerId },
        transport: {
          customerId,
          distanceUnit: 'KM', // TODO figure out where it is handle
          segments: segmentList,
        },
      }),
    {
      onSuccess({ data }) {
        setOffsets({
          ...offsets,
          stayOffset: data.hotel.value,
          ...getTransportOffsets(data.transport),
          total: data.total,
          totalOffsetInTonnes: data.totalToOffsetInTonnes,
        });
        setTotalOffsetInTonnes(data.totalToOffsetInTonnes);
      },
    }
  );

  const { data: equivalents } = useQuery(
    ['equivalents'],
    () => getEquivalents(totalOffsetInTonnes),
    {
      enabled: !!totalOffsetInTonnes,
      select: data => data.data,
    }
  );

  const { isLoading: isLoadingCOList, isFetching: isFetchingCOList } = useQuery(
    ['carbon-offset-projects'],
    () => getCarbonOffsetProjectList(customerId),
    {
      select: data => data.data,
      onSuccess(data) {
        const selectedCarbonProjects: SelectedCarbonOffsetProjectT[] = [];

        const parsedRootCarbonOffsetProjects = data.content.map(item => {
          const qty = carbonOffsets?.find(offset => offset.id === item.id)?.qty || 0;

          if (!!qty) {
            selectedCarbonProjects.push({
              id: item.id,
              name: item.name,
              selectedQty: qty,
              basePrice: item.basePrice,
              totalCharge: toFixedWithoutZeros(+item.basePrice * qty),
            });
          }

          return {
            ...item,
            selectedQty: qty,
            totalCharge: toFixedWithoutZeros(+item.basePrice * qty),
          };
        });

        setRootCarbonOffsetProjects(parsedRootCarbonOffsetProjects);
        setSelectedCarbonOffsetProjects(selectedCarbonProjects);
      },
    }
  );

  const onSelectCarbonOffset = ({
    selectedId,
    selectedQty,
  }: {
    selectedId: number;
    selectedQty: number;
  }) => {
    const updatedCarbonOffsetProjects = rootCarbonOffsetProjects?.map(item =>
      item.id === selectedId
        ? { ...item, selectedQty, totalCharge: toFixedWithoutZeros(+item.basePrice * selectedQty) }
        : item
    );
    const selectedCarbonOffsets = updatedCarbonOffsetProjects
      .filter(({ selectedQty: qty }) => qty)
      .map(({ id, name, selectedQty: qty, basePrice, totalCharge }) => ({
        id,
        name,
        selectedQty: qty,
        basePrice,
        totalCharge,
      }));

    setRootCarbonOffsetProjects(updatedCarbonOffsetProjects);
    setSelectedCarbonOffsetProjects(selectedCarbonOffsets);
    setPreparedCarbonOffsetQueryData(
      selectedCarbonOffsets.map(({ id, selectedQty: qty }) => ({ id, qty }))
    );
  };

  const goToPayment = () =>
    navigateTo(
      RoutesPathE.PAYMENT_DETAILS_PAGE,
      { carbonOffsets: preparedCarbonOffsetQueryData, sustainableItems: [] },
      true
    );

  const goToSustainableActions = () =>
    navigateTo(
      RoutesPathE.SUSTAINABLE_ACTIONS_PAGE,
      { carbonOffsets: preparedCarbonOffsetQueryData },
      true
    );

  const onClickBack = () => {
    if (!!segments?.length) {
      navigateTo(RoutesPathE.TRANSPORTATION_PAGE, {}, true);

      return;
    }

    navigateTo(RoutesPathE.HOME_PAGE);
  };

  useEffect(() => {
    let segmentList = segments;

    if (!!segmentList?.length) {
      // transform segments if PLANE was selected
      segmentList = segmentList.map(segment => {
        if (segment.transportType === TransportTypesE.PLANE) {
          return {
            ...omit(segment, 'vehicleType'),
            flightClass: segment.vehicleType,
          };
        }

        return segment;
      });
    }

    if (calculate === CalculatorTypeE.HOTEL) {
      getHotelFootprint();
    }

    if (calculate === CalculatorTypeE.TRANSPORT) {
      getTransportFootprint(segmentList);
    }

    if (calculate === CalculatorTypeE.HOTEL_AND_TRANSPORT) {
      getCommonCarbonFootprint(segmentList);
    }
  }, []);

  return {
    isLoading: isLoadingCOList || isFetchingCOList,
    carbonOffsetProjects: rootCarbonOffsetProjects,
    selectedCarbonOffsetProjects,
    offsets,
    equivalents: equivalents || [],
    hasSegments: !!segments,
    preparedCarbonOffsetQueryData,
    onSelectCarbonOffset,
    goToPayment,
    goToSustainableActions,
    onClickBack,
  };
};

export default useCarbonOffsets;
